import { useQuery } from "@tanstack/react-query";
import type {
  AdminAreaSuggestionView,
  AdminWalletTopUpRequestListResponse,
  IntegrationLogListResponse,
  StoreSubscriptionListResponse,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import { isoFromDate } from "@/lib/formatting/date-range-labels";
import type { AttentionInput, PaidServicesInput } from "../lib/attention";

/** Enough rows to find the oldest item and, usually, every pending one. */
const PAGE = 100;
/** Same window the Paid services page asks for, so the two share a cache entry. */
const PAID_SERVICES_MONTHS = 6;
/** The queue goes stale quietly; re-check while the tab is open. */
const REFRESH_MS = 60_000;

export type AttentionSourceKey = keyof AttentionInput;

export type AttentionSource = {
  key: AttentionSourceKey;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

/**
 * Reads every admin queue the dashboard ranks, each from the endpoint its own
 * page already uses. Sources are independent: one failing must not hide the
 * others, and must not read as "nothing to do" — so each reports its own state.
 *
 * `canSeePaidServices` gates the super-admin-only usage endpoint; the API
 * refuses anyone else, and asking anyway would surface a false error.
 */
export function useAttentionSources(canSeePaidServices: boolean) {
  const today = isoFromDate(new Date());
  const common = { refetchInterval: REFRESH_MS } as const;

  const failedLogs = useQuery({
    ...common,
    queryKey: queryKeys.admin.integrationLogs({
      status: "failed",
      dateFrom: today,
      limit: 1,
      offset: 0,
    }),
    queryFn: () =>
      apiJsonClient<IntegrationLogListResponse>(
        `${endpoints.admin.integrationLogs}?${new URLSearchParams({
          status: "failed",
          dateFrom: today,
          limit: "1",
          offset: "0",
        })}`,
      ),
  });

  const overdue = useQuery({
    ...common,
    queryKey: queryKeys.admin.storeSubscriptions({
      overdue: true,
      limit: PAGE,
      offset: 0,
    }),
    queryFn: () =>
      apiJsonClient<StoreSubscriptionListResponse>(
        `${endpoints.admin.storeSubscriptions}?overdue=true&limit=${PAGE}&offset=0`,
      ),
  });

  const wallet = useQuery({
    ...common,
    queryKey: queryKeys.admin.walletTopUpRequests({
      reviewStatus: "PENDING",
      limit: PAGE,
      offset: 0,
    }),
    queryFn: () =>
      apiJsonClient<AdminWalletTopUpRequestListResponse>(
        `${endpoints.admin.storeWalletTopUpRequests}?reviewStatus=PENDING&limit=${PAGE}&offset=0`,
      ),
  });

  const suggestions = useQuery({
    ...common,
    queryKey: queryKeys.admin.areaSuggestions({ status: "pending" }),
    queryFn: () =>
      apiJsonClient<AdminAreaSuggestionView[]>(
        `${endpoints.admin.areaSuggestions}?status=pending`,
      ),
  });

  const paid = useQuery({
    ...common,
    enabled: canSeePaidServices,
    queryKey: queryKeys.admin.paidServicesUsage(PAID_SERVICES_MONTHS),
    queryFn: () =>
      apiJsonClient<{ services: PaidServicesInput }>(
        `${endpoints.admin.paidServicesUsage}?months=${PAID_SERVICES_MONTHS}`,
      ),
  });

  const latest = failedLogs.data?.items[0];
  const input: AttentionInput = {
    failedIntegrations: failedLogs.data && {
      total: failedLogs.data.total,
      latest: latest
        ? {
            occurredAt: latest.occurredAt,
            provider: latest.provider,
            storeName: latest.storeName,
            error: latest.error,
          }
        : null,
    },
    overdueStores: overdue.data && {
      total: overdue.data.total,
      items: overdue.data.items,
    },
    walletTransfers: wallet.data && {
      total: wallet.data.total,
      items: wallet.data.items,
    },
    areaSuggestions: suggestions.data,
    paidServices: canSeePaidServices ? paid.data?.services : undefined,
  };

  const tracked: Array<{
    key: AttentionSourceKey;
    query: { isLoading: boolean; isError: boolean; refetch: () => unknown };
  }> = [
    { key: "failedIntegrations", query: failedLogs },
    { key: "overdueStores", query: overdue },
    { key: "walletTransfers", query: wallet },
    { key: "areaSuggestions", query: suggestions },
    ...(canSeePaidServices ? [{ key: "paidServices" as const, query: paid }] : []),
  ];
  const sources: AttentionSource[] = tracked.map(({ key, query }) => ({
    key,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  }));

  return { input, sources };
}
